import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Patient, Sex } from '@/types/api';
import type { PatientInput } from './api';

type Props = {
  busy: boolean;
  onSubmit: (input: PatientInput) => void;
  /** Editing an existing patient: the form starts from their details. */
  initial?: Patient;
  submitLabel?: string;
};

/** Digits only, as JMBG and LBO are stored. */
const digitsOf = (value: string) => value.replace(/[\s-]+/g, '');

/** Empty, or exactly `length` digits (spaces and dashes aside). */
const validDigits = (value: string, length: number) => {
  const digits = digitsOf(value);
  return !digits || new RegExp(`^\\d{${length}}$`).test(digits);
};

/**
 * New patient, or the personal details of an existing one. Everything the
 * protocol records is required (#23): one parent's name, birth date, sex,
 * address, phone, and JMBG or LBO.
 */
export function PatientForm({ busy, onSubmit, initial, submitLabel }: Props) {
  const { t } = useTranslation();
  const [lastName, setLastName] = useState(initial?.lastName ?? '');
  const [firstName, setFirstName] = useState(initial?.firstName ?? '');
  const [parentName, setParentName] = useState(initial?.parentName ?? '');
  const [birthDate, setBirthDate] = useState(initial?.birthDate ?? '');
  const [sex, setSex] = useState<Sex | ''>(initial?.sex ?? '');
  const [jmbg, setJmbg] = useState(initial?.jmbg ?? '');
  const [lbo, setLbo] = useState(initial?.lbo ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [missingId, setMissingId] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!(digitsOf(jmbg) || digitsOf(lbo))) {
      setMissingId(true);
      return;
    }
    onSubmit({
      lastName: lastName.trim(),
      firstName: firstName.trim(),
      parentName: parentName.trim(),
      birthDate,
      sex: sex || null,
      jmbg: digitsOf(jmbg) || null,
      lbo: digitsOf(lbo) || null,
      address: address.trim(),
      phone: phone.trim(),
      notes: notes.trim() || null,
    });
  };

  return (
    <form className="stack" onSubmit={submit}>
      <div className="field">
        <label htmlFor="p-last">{t('patients.form.lastName')}</label>
        <input
          id="p-last"
          className="input"
          required
          data-autofocus
          autoComplete="off"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="p-first">{t('patients.form.firstName')}</label>
        <input
          id="p-first"
          className="input"
          required
          autoComplete="off"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="p-parent">{t('patients.form.parentName')}</label>
        <input
          id="p-parent"
          className="input"
          required
          autoComplete="off"
          value={parentName}
          onChange={(e) => setParentName(e.target.value)}
        />
      </div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="p-birth">{t('patients.form.birthDate')}</label>
          <input
            id="p-birth"
            className="input"
            type="date"
            required
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="p-sex">{t('patients.form.sex')}</label>
          <select
            id="p-sex"
            className="input"
            required
            value={sex}
            onChange={(e) => setSex(e.target.value as Sex | '')}
          >
            <option value="" disabled>
              —
            </option>
            <option value="M">{t('patients.sex.M')}</option>
            <option value="F">{t('patients.sex.F')}</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="p-jmbg">{t('patients.form.jmbg')}</label>
          <input
            id="p-jmbg"
            className="input"
            inputMode="numeric"
            autoComplete="off"
            value={jmbg}
            aria-invalid={missingId || undefined}
            aria-describedby="p-id-hint"
            onChange={(e) => {
              setJmbg(e.target.value);
              setMissingId(false);
              e.target.setCustomValidity(
                validDigits(e.target.value, 13) ? '' : t('patients.form.jmbgInvalid')
              );
            }}
          />
        </div>
        <div className="field">
          <label htmlFor="p-lbo">{t('patients.form.lbo')}</label>
          <input
            id="p-lbo"
            className="input"
            inputMode="numeric"
            autoComplete="off"
            value={lbo}
            aria-invalid={missingId || undefined}
            aria-describedby="p-id-hint"
            onChange={(e) => {
              setLbo(e.target.value);
              setMissingId(false);
              e.target.setCustomValidity(
                validDigits(e.target.value, 11) ? '' : t('patients.form.lboInvalid')
              );
            }}
          />
        </div>
      </div>
      <p id="p-id-hint" className={`small ${missingId ? 'field__error' : 'muted'}`}>
        {t('patients.form.idHint')}
      </p>
      <div className="field">
        <label htmlFor="p-address">{t('patients.form.address')}</label>
        <input
          id="p-address"
          className="input"
          required
          autoComplete="off"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="p-phone">{t('patients.form.phone')}</label>
        <input
          id="p-phone"
          className="input"
          type="tel"
          inputMode="tel"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="p-notes">{t('patients.form.notes')}</label>
        <textarea
          id="p-notes"
          className="textarea"
          maxLength={1000}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
        {submitLabel ?? t('patients.form.submit')}
      </button>
    </form>
  );
}
